import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardActions,
  CardContent,
  IconButton,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FaRegTrashCan, FaUpload } from 'react-icons/fa6';
import { v4 as uuidv4 } from 'uuid';
import logger from '../utils/logger';

const FileEditUploadParse = observer(({ photos, title, onPhotosChange }) => {
  const [loading, setLoading] = useState(false);
  const [photosList, setPhotosList] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Инициализация списка фотографий
  useEffect(() => {
    if (photos && photos.length > 0) {
      logger.log('FileEditUploadParse: Инициализация фотографий', photos);
      
      // Преобразуем фотографии в удобный для отображения формат
      const formattedPhotos = photos.map((photo, index) => {
        // Определяем URL изображения из разных возможных полей
        const imageUrl = photo.imgurl || photo.imageUrl || photo.url || '';
        
        return {
          id: photo.id || `existing-${index}`,
          url: imageUrl,
          sortOrder: photo.sortOrder || index + 1,
          isExisting: true, // Помечаем существующие фотографии
          originalData: photo // Сохраняем оригинальные данные
        };
      });
      
      // Сортируем фотографии по sortOrder
      const sortedPhotos = [...formattedPhotos].sort((a, b) => a.sortOrder - b.sortOrder);
      setPhotosList(sortedPhotos);
    } else {
      setPhotosList([]);
    }
  }, [photos]);

  // Удаление фотографии
  const handleDeletePhoto = (photoId) => {
    logger.log('Запрос на удаление фото:', photoId);
    // Удаляем фото из локального состояния
    const updatedPhotos = photosList.filter(photo => photo.id !== photoId);
    setPhotosList(updatedPhotos);
    
    // Обновляем родительский компонент
    if (onPhotosChange) {
      const resultPhotos = updatedPhotos.map(photo => 
        photo.isExisting ? photo.originalData : {
          imgurl: photo.url,
          id: photo.id,
          sortOrder: photo.sortOrder,
          isNew: true
        }
      );
      onPhotosChange(resultPhotos);
    }
  };

  // Обработка клика по кнопке загрузки
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  // Обработка drag-and-drop событий
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesSelected(e.dataTransfer.files);
    }
  };

  // Обработка загрузки новых файлов
  const handleFileUpload = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      handleFilesSelected(event.target.files);
    }
    // Сбрасываем input file, чтобы можно было загрузить те же файлы повторно
    event.target.value = '';
  };

  // Общая функция обработки выбранных файлов
  const handleFilesSelected = async (files) => {
    setLoading(true);
    const newFiles = Array.from(files);
    
    if (newFiles.length === 0) {
      setLoading(false);
      return;
    }

    try {
      // Проверка типов файлов
      const imageFiles = newFiles.filter(file => 
        file.type.startsWith('image/') || 
        /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(file.name)
      );
      
      if (imageFiles.length !== newFiles.length) {
        logger.warn('Некоторые выбранные файлы не являются изображениями и будут пропущены.');
      }
      
      // Обрабатываем каждый файл
      const newPhotosPromises = imageFiles.map(async (file, index) => {
        // Создаем локальный URL для предпросмотра
        const url = URL.createObjectURL(file);
        
        return {
          id: `new-${uuidv4()}`,
          url,
          file, // Сохраняем оригинальный файл для последующей загрузки
          sortOrder: photosList.length + index + 1,
          isNew: true,
          fileName: file.name,
          fileSize: file.size
        };
      });

      const newPhotos = await Promise.all(newPhotosPromises);
      
      // Добавляем новые фото к существующим
      const updatedPhotos = [...photosList, ...newPhotos];
      setPhotosList(updatedPhotos);
      
      // Обновляем родительский компонент
      if (onPhotosChange) {
        const resultPhotos = updatedPhotos.map(photo => 
          photo.isExisting ? photo.originalData : {
            imgurl: photo.url,
            id: photo.id,
            sortOrder: photo.sortOrder,
            file: photo.file,
            isNew: true,
            fileName: photo.fileName
          }
        );
        onPhotosChange(resultPhotos);
      }
    } catch (error) {
      logger.error('Ошибка обработки файлов:', error);
    } finally {
      setLoading(false);
    }
  };

  // Получить понятный размер файла в KB или MB
  const getReadableFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h3">
          {title?.label || 'Фотографии'}
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<FaUpload />}
          onClick={handleUploadClick}
          disabled={loading}
        >
          Загрузить фото
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          multiple
          onChange={handleFileUpload}
        />
      </Box>
      
      {/* Зона Drag & Drop */}
      <Box
        ref={dropZoneRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: isDragging ? '2px dashed #3f51b5' : '2px dashed #ccc',
          borderRadius: 1,
          p: 3,
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDragging ? 'rgba(63, 81, 181, 0.08)' : 'transparent',
          transition: 'all 0.3s',
          cursor: 'pointer'
        }}
        onClick={handleUploadClick}
      >
        <FaUpload style={{ fontSize: '2rem', marginBottom: '8px', color: isDragging ? '#3f51b5' : '#999' }} />
        <Typography variant="body1" align="center">
          {isDragging ? 'Отпустите, чтобы загрузить' : 'Перетащите изображения сюда или нажмите для выбора'}
        </Typography>
        <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
          Поддерживаются JPG, PNG, GIF и другие форматы изображений
        </Typography>
      </Box>
      
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" my={2}>
          <CircularProgress size={40} sx={{ mr: 2 }} />
          <Typography>Обработка изображений...</Typography>
        </Box>
      )}
      
      {photosList.length > 0 ? (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Всего фотографий: {photosList.length}
          </Typography>
          <Grid container spacing={2}>
            {photosList.map((photo) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={photo.id}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  position: 'relative',
                  boxShadow: photo.isNew ? '0 0 0 2px #4caf50' : 'inherit',
                }}>
                  {photo.isNew && (
                    <Chip 
                      label="Новое" 
                      size="small" 
                      color="success" 
                      sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}
                    />
                  )}
                  <CardMedia
                    component="img"
                    height="200"
                    image={photo.url}
                    alt={`Фото ${photo.id}`}
                    sx={{ objectFit: 'cover' }}
                  />
                  {photo.isNew && photo.fileName && (
                    <CardContent sx={{ pt: 1, pb: 0, px: 1, flexGrow: 1 }}>
                      <Typography variant="body2" noWrap title={photo.fileName}>
                        {photo.fileName}
                      </Typography>
                      {photo.fileSize && (
                        <Typography variant="caption" color="textSecondary">
                          {getReadableFileSize(photo.fileSize)}
                        </Typography>
                      )}
                    </CardContent>
                  )}
                  <CardActions sx={{ p: 1, justifyContent: 'flex-end', mt: 'auto' }}>
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeletePhoto(photo.id)}
                      title="Удалить"
                    >
                      <FaRegTrashCan />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Typography color="textSecondary" align="center" sx={{ my: 2 }}>
          Нет фотографий для отображения
        </Typography>
      )}
    </Box>
  );
});

export default FileEditUploadParse;
